import { useEffect, useState, useCallback } from "react";
import { Plane, Loader2, Star, Eye, EyeOff, Trash2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const TABS = [
  ["all", "All travel"], ["flight", "Flights"], ["train", "Trains"], ["package", "Holiday Packages"],
  ["bus", "Bus"], ["hotel", "Hotels"], ["cab", "Cabs"],
];

const statusPill = (s) => {
  if (s.status === "published" && s.active) return ["Live", "bg-emerald-50 text-emerald-700"];
  if (s.status === "published" && !s.active) return ["Hidden", "bg-amber-50 text-amber-700"];
  if (s.status === "pending") return ["Pending", "bg-sky-50 text-sky-700"];
  if (s.status === "rejected") return ["Rejected", "bg-rose-50 text-rose-700"];
  return [s.status || "Draft", "bg-slate-100 text-slate-600"];
};

export default function AdminTravelListings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/travel-listings", { params: tab === "all" ? {} : { travel_type: tab } })
      .then((r) => setData(r.data))
      .catch(() => setData({ listings: [], counts: {} }))
      .finally(() => setLoading(false));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, fn, okMsg) => {
    setBusy(id);
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };
  const publish = (s) => act(s.service_id, () => api.post(`/admin/services/${s.service_id}/decision`, { action: "approve" }), "Published");
  const setActive = (s, active) => act(s.service_id, () => api.post(`/admin/services/${s.service_id}/set-active`, { active }), active ? "Now visible" : "Hidden from shoppers");
  const feature = (s) => act(s.service_id, () => api.post(`/admin/services/${s.service_id}/feature`), s.featured ? "Unfeatured" : "Featured");
  const remove = (s) => { if (!window.confirm(`Remove "${s.name}" permanently? Use Hide to only take it offline.`)) return; act(s.service_id, () => api.delete(`/admin/services/${s.service_id}`), "Listing removed"); };

  const counts = data?.counts || {};
  const listings = data?.listings || [];

  return (
    <div data-testid="admin-travel-listings-page">
      <PageHeader title="Travel & Holidays" icon={Plane} subtitle="Every flight, train and holiday-package listing across all sellers — publish, hide, feature or remove. Includes preloaded sample content." />

      <div className="mb-4 flex flex-wrap gap-2" data-testid="travel-listing-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`travel-tab-${k}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${tab === k ? "bg-sky-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {label}{counts[k] != null ? ` (${counts[k]})` : ""}
          </button>
        ))}
      </div>

      {loading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        listings.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="travel-listings-empty">
            <Plane className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-700">No listings in this view</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="travel-listings-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="p-3">Listing</th><th className="p-3">Type</th><th className="p-3">Seller</th>
                  <th className="p-3">Route / detail</th><th className="p-3">Status</th><th className="p-3 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((s) => {
                  const [pl, cls] = statusPill(s);
                  return (
                    <tr key={s.service_id} className="border-t border-slate-50" data-testid={`travel-listing-${s.service_id}`}>
                      <td className="p-3">
                        <p className="font-medium text-slate-800">{s.name}{s.featured && <Star className="ml-1 inline h-3.5 w-3.5 fill-amber-400 text-amber-400" />}</p>
                        {s.demo_seed && <span className="text-[10px] font-semibold text-slate-400">Sample content</span>}
                      </td>
                      <td className="p-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold capitalize text-slate-600">{s.travel_type}</span></td>
                      <td className="p-3 text-slate-600">{s.seller?.name || s.seller?.email || "—"}</td>
                      <td className="p-3 text-xs text-slate-500">
                        {s.route || (s.themes || []).slice(0, 2).join(", ") || "—"}
                        {s.departures_count ? <span className="ml-1 text-slate-400">· {s.departures_count} dep.</span> : null}
                        {s.days ? <span className="ml-1 text-slate-400">· {s.days}D</span> : null}
                      </td>
                      <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`travel-status-${s.service_id}`}>{pl}</span></td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {s.status === "pending" && <button onClick={() => publish(s)} disabled={busy === s.service_id} data-testid={`travel-publish-${s.service_id}`} className="rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-emerald-700">Publish</button>}
                          {s.status === "published" && (s.active
                            ? <button onClick={() => setActive(s, false)} disabled={busy === s.service_id} data-testid={`travel-hide-${s.service_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"><EyeOff className="h-3 w-3" /> Hide</button>
                            : <button onClick={() => setActive(s, true)} disabled={busy === s.service_id} data-testid={`travel-show-${s.service_id}`} className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-sky-700"><Eye className="h-3 w-3" /> Show</button>)}
                          <button onClick={() => feature(s)} disabled={busy === s.service_id} data-testid={`travel-feature-${s.service_id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ${s.featured ? "bg-amber-500 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}><Star className="h-3 w-3" /> {s.featured ? "Featured" : "Feature"}</button>
                          {s.slug && <a href={`/service/${s.slug}`} target="_blank" rel="noreferrer noopener noreferrer" data-testid={`travel-view-${s.service_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"><ExternalLink className="h-3 w-3" /> View</a>}
                          <button onClick={() => remove(s)} disabled={busy === s.service_id} data-testid={`travel-remove-${s.service_id}`} className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-2.5 py-1 text-[10px] font-semibold text-rose-600 hover:bg-rose-50"><Trash2 className="h-3 w-3" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
